import type {
  QueryFunctionContext,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import groq from 'groq';
import { useIsSkincare } from 'hooks/useIsSkincare';
import {
  ASSET,
  BANNER_BLOCK,
  BLOG_CAROUSEL_BLOCK,
  CATEGORIES_INTRO_BLOCK,
  COMPARISON_TABLE_BLOCK,
  FACTS_BLOCK,
  FAQ_BLOCK,
  FEATURE_LIST_BLOCK,
  ICONS_LIST_BLOCK,
  MITOPURE_TRANSFORMATION_BLOCK,
  PRESS_QUOTES_BLOCK,
  PRODUCT_BLOCK,
  PRODUCT_CAROUSEL_BLOCK,
  PRODUCT_COMPARISON_BLOCK,
  PRODUCT_SLUG,
  PRODUCTS_IN_COLLECTION,
  QUOTE_CAROUSEL_BLOCK,
  REVIEWS_CAROUSEL_BLOCK,
  TESTIMONIAL_GROUP_BLOCK,
  TEXT_BLOCK,
  TEXT_MEDIA_SPLIT_BLOCK,
  VIBE_GALLERY_BLOCK,
  WALL_OF_LOVE_BLOCK,
} from 'lib/queries';
import { SOCIAL_MEDIA_CAROUSEL_BLOCK } from 'lib/queries/blocks/socialMediaCarousel';
import { client } from 'lib/sanity';
import type { ProductListingPage } from 'types/cms/pages/ProductListingPage';
import type { SiteCategory } from 'types/common/SiteCategory';

type ProductListingPageQuery = UseQueryResult & {
  data: ProductListingPage;
};

export const PRODUCT_LISTING_PAGE_KEY = 'productListingPage';
export const SHOP_ALL = null;
export type ShopSlug = string | typeof SHOP_ALL;

const SUPPLEMENTS_PLP_ID = 'plp';
const SKINCARE_PLP_ID = 'skincare_plp';

type ProductListingPageQueryKey = [string, ShopSlug, SiteCategory];

export const fetchProductListingPage = async ({
  queryKey,
}: QueryFunctionContext<ProductListingPageQueryKey>) => {
  const [, slug, siteCategory] = queryKey;

  const id = siteCategory === 'skincare' ? SKINCARE_PLP_ID : SUPPLEMENTS_PLP_ID;

  const slugFilter = '*[_type == $type && slug.current == $slug][0]';
  const idFilter = '*[_type == $type && _id == $id][0]';

  const filter = slug ? slugFilter : idFilter;

  const query = groq`
    ${filter} {
      seo,
      title,
      "isMarqueeVisible": coalesce(isMarqueeVisible, false),
      primaryCollection {
        ${PRODUCTS_IN_COLLECTION}
      },
      featuredProduct {
        image {
          ${ASSET}
        },
        description,
        product -> {
          ${PRODUCT_SLUG}
        }
      },
      blocks[]{
        _key,
        _ref,
        _type,
        ${FACTS_BLOCK},
        ${BANNER_BLOCK},
        ${TEXT_MEDIA_SPLIT_BLOCK},
        ${PRODUCT_CAROUSEL_BLOCK},
        ${QUOTE_CAROUSEL_BLOCK},
        ${TESTIMONIAL_GROUP_BLOCK},
        ${FAQ_BLOCK},
        ${PRESS_QUOTES_BLOCK},
        ${MITOPURE_TRANSFORMATION_BLOCK},
        ${COMPARISON_TABLE_BLOCK},
        ${CATEGORIES_INTRO_BLOCK},
        ${ICONS_LIST_BLOCK},
        ${PRODUCT_BLOCK},
        ${WALL_OF_LOVE_BLOCK},
        ${BLOG_CAROUSEL_BLOCK},
        ${PRODUCT_COMPARISON_BLOCK},
        ${SOCIAL_MEDIA_CAROUSEL_BLOCK},
        ${FEATURE_LIST_BLOCK},
        ${TEXT_BLOCK},
        ${VIBE_GALLERY_BLOCK},
        ${REVIEWS_CAROUSEL_BLOCK}
      }
    }
  `;

  const plp: ProductListingPage = await client.fetch(query, {
    type: 'timeline_plp',
    slug,
    id,
  });

  return plp;
};

export const useProductListingPage = (slug: ShopSlug) => {
  const isSkincare = useIsSkincare();
  const siteCategory: SiteCategory = isSkincare ? 'skincare' : 'supplements';

  return useQuery({
    queryKey: [PRODUCT_LISTING_PAGE_KEY, slug, siteCategory],
    queryFn: fetchProductListingPage,
  }) as ProductListingPageQuery;
};
